import { FC, useMemo, useState } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { TbArrowsExchange, TbChartLine } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import { FinancialValue } from '@/shared/components/financial-values';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { dayjs } from '@/utilities';

import type { FinancialOverviewSecurityPriceDropdownProps } from '../../types';

const normalizedCurrency = (currency?: string | null) => {
    const normalized = currency?.trim().toUpperCase();

    return normalized && /^[A-Z]{3}$/.test(normalized) ? normalized : 'USD';
};

const SecurityPrice: FC<{ currency?: string | null; price: number }> = ({ currency, price }) => {
    const resolvedCurrency = normalizedCurrency(currency);

    return (
        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <FinancialValue currency={resolvedCurrency} scalable={false} value={price} />
        </span>
    );
};

export const FinancialOverviewSecurityPriceDropdown: FC<FinancialOverviewSecurityPriceDropdownProps> = ({
    companyName,
    prices,
    updatedAt,
    website,
}) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const alternativePrices = prices.filter((price) => !price.current);
    const filteredPrices = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return prices;
        }

        return prices.filter((price) =>
            [price.exchange, price.name, price.stockExchange, price.symbol, price.type].some((value) =>
                value?.toLowerCase().includes(query),
            ),
        );
    }, [prices, search]);

    if (!alternativePrices.length) {
        return null;
    }

    return (
        <Dropdown
            arrow={false}
            destroyOnHidden
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);

                if (!nextOpen) {
                    setSearch('');
                }
            }}
            open={open}
            popupRender={() => (
                <DropdownPanel
                    footer={
                        <LearnMoreFooter topic='share classes and alternate listed securities' variant='dropdown' />
                    }
                    maxHeight='min(28rem, 70vh)'
                    search={{
                        ariaLabel: 'Search security prices',
                        placeholder: 'Search Securities Or Exchanges...',
                        setValue: setSearch,
                        value: search,
                    }}
                    width={16 * 27}
                >
                    <DropdownMenuLabel
                        extra={
                            <small style={{ color: token.colorTextQuaternary }}>
                                {updatedAt ? `Updated ${dayjs(updatedAt).format('LT')}` : 'Live quotes'}
                            </small>
                        }
                        icon={<TbArrowsExchange size='0.875rem' />}
                    >
                        Security Prices
                    </DropdownMenuLabel>
                    <DropdownPanelMenu selectable={false}>
                        {filteredPrices.length ? (
                            filteredPrices.map((price) => (
                                <Menu.Item
                                    key={`${price.exchange ?? 'exchange'}:${price.symbol}`}
                                    onClick={() => setOpen(false)}
                                >
                                    <a
                                        href={`/search/companies/${encodeURIComponent(price.symbol)}/overview`}
                                        style={{ color: 'inherit' }}
                                    >
                                        <DropdownOptionContent
                                            avatar={{
                                                icon: <TbChartLine size='0.875rem' />,
                                                website,
                                            }}
                                            extra={<SecurityPrice currency={price.currency} price={price.price} />}
                                            metadata={[
                                                price.stockExchange ?? price.exchange,
                                                price.type ??
                                                    (price.current ? 'Current security' : 'Alternate security'),
                                            ]}
                                            selected={price.current}
                                            singleLine
                                            title={
                                                <>
                                                    <strong>{price.exchange ?? 'Exchange'}</strong>: {price.symbol}
                                                </>
                                            }
                                        />
                                    </a>
                                </Menu.Item>
                            ))
                        ) : (
                            <EmptySection
                                description='No listed security matches this search.'
                                height='10rem'
                                icon={<TbArrowsExchange size='1.25rem' />}
                                label='No Securities Found'
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
            placement='bottomRight'
            trigger={['click']}
        >
            <Button
                aria-label={`View live prices for ${companyName ?? 'this company'} securities`}
                icon={<TbArrowsExchange size='1rem' style={{ color: token.colorTextTertiary, display: 'block' }} />}
                size='small'
                title='View security prices'
                type='dashed'
            />
        </Dropdown>
    );
};
